import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react'

import { ErrorIconSmall } from '@hm-group/fabric-icons/icons/ErrorIcon'
import { InputValidIconSmall } from '@hm-group/fabric-icons/icons/InputValidIcon'
import cn from 'classnames'

import { MetaText, FormLabel } from 'components/Typography'

import styles from './TextArea.module.scss'
import { TextAreaProps } from './types'

const TextArea: React.FC<TextAreaProps> = forwardRef(
  (
    {
      label,
      name,
      id,
      placeholder,
      helperText,
      className,
      isRequired,
      isInvalid,
      isSuccess,
      isDisabled,
      value,
      onChange,
      maxLength,
      inputMode = 'none',
      hasDataTrackingPii,
      testId,
    },
    ref,
  ) => {
    const innerRef = useRef<HTMLTextAreaElement>(null)

    useImperativeHandle<HTMLTextAreaElement | null, HTMLTextAreaElement | null>(
      ref,
      () => innerRef.current,
    )

    const [isScrollable, setIsScrollable] = useState<boolean>(false)

    const afterContent = isSuccess ? (
      <InputValidIconSmall data-testid={`${name}-valid-after-content`} />
    ) : isInvalid ? (
      <ErrorIconSmall data-testid={`${name}-invalid-after-content`} />
    ) : null

    useEffect(() => {
      if (innerRef) {
        if (innerRef.current) {
          setIsScrollable(innerRef.current.scrollHeight > innerRef.current.clientHeight)
        }
      }
    }, [value])

    return (
      <div className={className}>
        <FormLabel
          htmlFor={id || name}
          className={cn(styles.label, isDisabled && styles.label_disabled)}
          isRequired={isRequired}
        >
          {label}
        </FormLabel>
        <div className={styles.textareaContainer}>
          <textarea
            ref={innerRef}
            id={id || name}
            name={name}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            className={cn(
              styles.textarea,
              isDisabled && styles.textarea_disabled,
              isInvalid && styles.textarea_invalid,
              isSuccess && styles.textarea_success,
              isScrollable && styles.textarea_scrollable,
            )}
            disabled={isDisabled}
            required={isRequired}
            maxLength={maxLength}
            aria-invalid={isInvalid}
            aria-errormessage={isInvalid ? `${name}-message` : undefined}
            inputMode={inputMode}
            data-tracking-pii={hasDataTrackingPii}
            data-testid={testId}
          />
          {afterContent && (
            <div
              className={cn(
                styles.afterContent,
                isScrollable && styles.afterContent_scrollable,
                isSuccess && styles.afterContent_success,
                isInvalid && styles.afterContent_invalid,
              )}
            >
              {afterContent}
            </div>
          )}
        </div>
        <MetaText
          Tag="p"
          id={`${name}-message`}
          role="alert"
          className={cn(styles.helperText, isInvalid && styles.helperText_invalid)}
        >
          {helperText}
        </MetaText>
      </div>
    )
  },
)

export default TextArea
